import React, { useState } from "react";
import { InputLabel, Stack, ThemeProvider, useMediaQuery } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { Controller, useFormContext } from "react-hook-form";
import moment from "moment";

import { theme } from "../../Utils/theme";

import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from "../../Assets/Icons";

// const shortcutsItems = [
//   {
//     label: "Today",
//     getValue: () => moment(),
//     disabled: true,
//   },
//   {
//     label: "Tomorrow",
//     getValue: () => {
//       const today = moment();
//       return today.add(1, "days");
//     },
//   },
//   {
//     label: "Today + 2",
//     getValue: () => {
//       const today = moment();
//       return today.add(2, "day");
//     },
//   },
// ];

export default function DatePickerField({
  name,
  label,
  format = "MM/DD/YYYY",
  disabled = false,
  validations = {},
  datePickerProps = {},
  textFieldProps = {},
  miniField = false,
  defaultValue = null,
  required = false,
  slots = {},
  slotProps = {},
  variant = "outlined",
}) {
  const [openDatePicker, setOpenDatePicker] = useState(false);
  const { control } = useFormContext();

  const generatedId = React.useId();
  const inputId = name ? `dpf-${name}` : `dpf-${generatedId}`;
  const labelId = label ? `${inputId}-label` : undefined;

  const isTouchScreen = useMediaQuery("@media (pointer: coarse)");

  return (
    <Controller
      name={name}
      control={control}
      rules={{
        required,
        validate: validations,
      }}
      defaultValue={defaultValue?.format("YYYY-MM-DD")}
      render={({ field: { value, onChange }, fieldState: { error } }) => {
        const handleChange = (v) => {
          if (v?.isValid()) {
            onChange(v.format("YYYY-MM-DD"));
          } else {
            onChange(v?._i ?? null);
          }
        };

        return (
          <ThemeProvider theme={theme}>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <Stack className="custom-date-picker">
                {!!label && (
                  <InputLabel id={labelId} htmlFor={inputId} sx={getInputLabelSxProp(variant)}>
                    {label}
                  </InputLabel>
                )}
                <DatePicker
                  control={control}
                  format={format}
                  defaultValue={defaultValue}
                  disabled={disabled}
                  value={!!value ? moment(value) : null}
                  open={openDatePicker}
                  onChange={handleChange}
                  onOpen={() => setOpenDatePicker(true)}
                  onClose={() => setOpenDatePicker(false)}
                  slots={{
                    openPickerIcon: CalendarIcon,
                    leftArrowIcon: ChevronLeft,
                    rightArrowIcon: ChevronRight,
                    ...slots,
                  }}
                  slotProps={{
                    textField: {
                      ...textFieldProps,
                      id: inputId,
                      readOnly: miniField,
                      error: !!error,
                      className: "custom-date-picker-field",
                      helperText: error?.message,
                      inputProps: {
                        ...(label
                          ? { "aria-labelledby": labelId }
                          : { "aria-label": textFieldProps?.placeholder || `Date (${format})` }),
                        ...(textFieldProps?.inputProps || {}),
                      },
                      sx: getDatePickerTextFieldSxProp({
                        miniField,
                        isTouchScreen,
                        variant,
                        pickerDisabled: datePickerProps?.disableOpenPicker,
                      }),
                      onClick: () => setOpenDatePicker(true),
                    },
                    // shortcuts: {
                    //   items: shortcutsItems,
                    //   sx: shortcutsSxProp,
                    // },
                    openPickerButton: { disableRipple: true, sx: getOpenDatePickerButtonSxProp(miniField) },
                    toolbar: {
                      hidden: true,
                    },
                    popper: {
                      placement: "bottom-end",
                    },
                    desktopPaper: {
                      sx: paperSxProp,
                    },
                    mobilePaper: {
                      sx: paperSxProp,
                    },
                    calendarHeader: {
                      sx: calendarHeaderSxProp,
                    },
                    previousIconButton: { disableRipple: true },
                    nextIconButton: { disableRipple: true },
                    yearButton: { sx: yearSxProp },
                    day: { sx: daySxProp },
                    actionBar: { sx: actionBarSxProp },
                    ...slotProps,
                  }}
                  {...datePickerProps}
                />
              </Stack>
            </LocalizationProvider>
          </ThemeProvider>
        );
      }}
    />
  );
}

const getDatePickerTextFieldSxProp = ({ miniField, isTouchScreen, variant, pickerDisabled }) => ({
  cursor: "pointer",
  "& .MuiOutlinedInput-root": {
    backgroundColor: "transparent",
    paddingRight: "0",
    ...(miniField
      ? {
          width: isTouchScreen ? "200px" : "240px",
        }
      : { height: variant === "outlined" ? "37px" : variant === "filled" ? "50px" : "0" }),

    ...(variant === "filled"
      ? {
          "&:not(.Mui-disabled):is(:hover,.Mui-focused) .MuiOutlinedInput-input": {
            borderColor: "rgb(from var(--primary-400) r g b / 80%)",
          },

          "&:not(.Mui-disabled).Mui-focused .MuiOutlinedInput-input": {
            boxShadow: "0 0 0 3.5px rgb(from var(--primary-400) r g b / 16%)",
          },

          "&:not(.Mui-disabled).Mui-error .MuiOutlinedInput-input": {
            background: "rgb(from var(--error-red) r g b / 4%)",
            borderColor: "rgb(from var(--error-red) r g b / 80%)",
            boxShadow: "none",
          },
        }
      : { height: variant === "outlined" ? "37px" : variant === "filled" ? "50px" : "0" }),

    ...(variant === "filled"
      ? {
          "&:not(.Mui-disabled):is(:hover,.Mui-focused) .MuiOutlinedInput-input": {
            borderColor: "rgb(from var(--primary-400) r g b / 80%)",
          },

          "&:not(.Mui-disabled).Mui-focused .MuiOutlinedInput-input": {
            boxShadow: "0 0 0 3.5px rgb(from var(--primary-400) r g b / 16%)",
          },

          "&:not(.Mui-disabled).Mui-error .MuiOutlinedInput-input": {
            background: "rgb(from var(--error-red) r g b / 4%)",
            borderColor: "rgb(from var(--error-red) r g b / 80%)",
            boxShadow: "none",
          },
        }
      : {}),

    "&.Mui-disabled .MuiOutlinedInput-input": {
      textFillColor: "rgb(from var(--american-silver) r g b / 80%)",
      cursor: "not-allowed",
      ...(variant === "filled" ? { opacity: 0.6 } : {}),
    },
    "& .MuiOutlinedInput-input": {
      boxSizing: "border-box",
      borderRadius: "0",
      padding: miniField ? "10px 12px" : variant === "outlined" ? "8px 0" : variant === "filled" ? "12px 16px" : "0",
      font: miniField
        ? "normal normal 500 11px/16px Poppins, sans-serif"
        : variant === "outlined"
          ? "normal normal 500 14px/20px Poppins, sans-serif"
          : variant === "filled"
            ? "normal normal 500 13px/16px Poppins, sans-serif"
            : "",
      color: "var(--mist)",
      height: miniField || variant === "outlined" ? "36px" : variant === "filled" ? "48px" : "0",
      textOverflow: "ellipsis",
      ...(miniField
        ? {
            borderRadius: "var(--rounded-md)",
            border: "1px solid rgb(from var(--mist) r g b / 30%)",
            ...(!isTouchScreen && !pickerDisabled ? { paddingRight: "40px" } : {}),
          }
        : variant === "outlined"
          ? {
              border: "none",
              borderBottom: "1px solid rgb(from var(--mist) r g b / 30%)",
              ...(!isTouchScreen && !pickerDisabled ? { paddingRight: "28px" } : {}),
            }
          : variant === "filled"
            ? {
                borderRadius: "var(--rounded-md)",
                border: "1px solid rgb(from var(--mist) r g b / 10%)",
                background: "rgb(from var(--bg-secondary) r g b / 30%)",
                ...(!isTouchScreen && !pickerDisabled ? { paddingRight: "40px" } : {}),
              }
            : {}),
      "&::placeholder": {
        color: "var(--mist)",
        opacity: 0.9,
      },
    },
    "+ .MuiFormHelperText-root": {
      margin: "6px 0 0",
      font: "normal normal 400 12px/16px Poppins, sans-serif",
      color: "var(--error-red)",
    },
    "& .MuiInputAdornment-root": {
      marginLeft: 0,
      "& .MuiIconButton-root": {
        position: "absolute",
        top: "calc(50% - 8px)",
        right: !isTouchScreen
          ? miniField
            ? "12px"
            : variant === "outlined"
              ? "0"
              : variant === "filled"
                ? "12px"
                : "0"
          : "0",
        height: "16px",
        width: "16px",
        "& svg path": {
          fill: variant === "outlined" ? "var(--primary-400)" : variant === "filled" ? "var(--mist)" : "",
        },
      },
    },
    "& .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
  },
});

const getOpenDatePickerButtonSxProp = (miniField) => ({
  height: miniField ? "16px" : "20px",
  width: miniField ? "16px" : "20px",
  padding: 0,
  marginRight: 0,
});

const calendarHeaderSxProp = {
  position: "relative",
  margin: 0,
  minHeight: "36px",
  borderBottom: "1px solid rgb(from var(--mist) r g b / 20%)",
  paddingBottom: "46px",
  marginBottom: "12px",
  "& .MuiPickersArrowSwitcher-root": {
    width: 0,
  },
  "& .MuiPickersCalendarHeader-labelContainer": {
    margin: "auto",
    font: "normal normal 600 18px/24px Poppins, sans-serif",
    color: "var(--mist)",
    "& .MuiPickersCalendarHeader-switchViewIcon path": {
      fill: "var(--mist)",
    },
  },
  "& .MuiPickersArrowSwitcher-button": {
    position: "absolute",
    top: "8px",
    bottom: 0,
    height: "20px",
    width: "20px",
    padding: 0,
    "&.Mui-disabled": { opacity: "0.3" },
  },
  "& .MuiPickersArrowSwitcher-previousIconButton": {
    left: 0,
    "& svg path": { fill: "var(--mist)" },
  },
  "& .MuiPickersArrowSwitcher-nextIconButton": {
    right: 0,
    "& svg path": { fill: "var(--mist)" },
  },
};

const paperSxProp = {
  padding: "20px",
  borderRadius: "var(--rounded-2xl)",
  border: "var(--card-border)",
  background: "var(--bg-primary)",
  boxShadow: "0px 0px 7px 2px rgba(0, 0, 0, 0.05)",
  "& .MuiDialogContent-root": {
    overflow: "hidden",
  },
  "& .MuiDateCalendar-root": {
    width: "auto",
    height: "329px",
    "& .MuiDayCalendar-header .MuiDayCalendar-weekDayLabel": {
      font: "normal normal 600 14px/20px Poppins, sans-serif",
      color: "var(--american-silver)",
    },
    "& .MuiDayCalendar-slideTransition": {
      minHeight: "230px",
    },
    "& .MuiYearCalendar-root": { width: "280px" },
  },
};

export const daySxProp = {
  font: "normal normal 500 14px/20px Poppins, sans-serif",
  color: "var(--mist)",
  "&.MuiPickersDay-today": { border: "none" },
  "&.Mui-selected": { color: "var(--charcoal)", backgroundColor: "var(--primary-400) !important" },
  "&.Mui-disabled:not(&.Mui-selected)": { color: "var(--american-silver)", opacity: "0.3" },
  "&.MuiPickersDay-today:not(&.Mui-selected):not(&.Mui-disabled)": { color: "var(--primary-400)" },
  "&.MuiPickersDay-dayOutsideMonth": { color: "var(--american-silver)", opacity: "0.6" },
  "&:not(&.Mui-selected):not(&.Mui-disabled):hover": { backgroundColor: "rgb(from var(--mist) r g b / 10%)" },
  "&:not(&.Mui-selected):not(&.Mui-disabled):focus": { backgroundColor: "rgb(from var(--mist) r g b / 20%)" },
};

const yearSxProp = {
  font: "normal normal 500 14px/20px Poppins, sans-serif",
  width: "64px",
  color: "var(--mist)",
  "&.Mui-disabled": { color: "var(--american-silver)", opacity: "0.3" },
  "&.Mui-selected": { backgroundColor: "var(--primary-400) !important", color: "var(--charcoal)" },
  "&:not(&.Mui-selected):not(&.Mui-disabled):hover": { backgroundColor: "rgb(from var(--mist) r g b / 10%)" },
  "&:not(&.Mui-selected):not(&.Mui-disabled):focus": { backgroundColor: "rgb(from var(--mist) r g b / 20%)" },
};

const actionBarSxProp = {
  padding: "12px 0 0",
  // borderTop: "1px solid rgb(from var(--mist) r g b / 20%)",

  "&.MuiPickersLayout-actionBar": {
    gridRow: "5 !important",
    gridColumn: "2 !important",
    gap: "16px",
    "& .MuiButton-root": {
      font: "normal normal 600 14px/20px Poppins, sans-serif",
      color: "var(--primary-400)",
      padding: 0,
      marginLeft: 0,
      minWidth: "auto",
    },
  },
};

const getInputLabelSxProp = (variant) => ({
  display: "inline-block",
  overflow: "visible",
  ...(variant === "outlined"
    ? { font: "normal normal 400 12px/16px Poppins, sans-serif", color: "var(--primary-400)", margin: "0" }
    : variant === "filled"
      ? { font: "normal normal 500 13px/16px Poppins, sans-serif", color: "var(--mist)", marginBottom: "12px" }
      : {}),
});

// const shortcutsSxProp = {
//   "&.MuiPickersLayout-shortcuts": {
//     gridRow: "4 !important",
//     gridColumn: "2  !important",
//     display: "flex",
//     justifyContent: "center",
//     gap: "16px",
//     "&.MuiList-root": {
//       padding: "12px 0",
//       "& .MuiListItem-root": {
//         flex: "0 0 0",
//         padding: "0",
//         "& .MuiButtonBase-root": {
//           font: "normal normal 500 12px/16px Poppins, sans-serif",
//           height: "24px",
//           borderRadius: "var(--rounded-2xl)",
//           background: "var(--mist)",
//           "& .MuiChip-label": { padding: "4px 16px" },
//         },
//       },
//     },
//   },
// };
